import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateUUID } from '@/utils/uuid';
import { GlassType } from '@/types/glassTypes';
import { EstimateImage, saveImage, getImagesForEstimate, deleteImage, updateImageDescription, deleteImagesForEstimate } from '@/services/imageService';

// Define types for estimate items
export interface EstimateItem {
  id: string;
  glassType: GlassType;
  width: number;
  height: number;
  quantity: number;
  measurementType: 'DLO' | 'Glass Size';
  frameType: 'Storefront' | 'Curtain Wall';
  curtainWallType?: 'Regular' | 'SSG';
  adjustedWidth?: number;
  adjustedHeight?: number;
  // DLO addition option for 1/4" glass in Storefront systems
  storefrontDLOAddition?: '1/2' | '5/8';
  // Options for insulated glass units
  hasArgonGas?: boolean;
  hasAluminumSpacer?: boolean;
  aluminumSpacerColor?: 'Black' | 'Bronze' | 'Mill';
  notes?: string[];
}

// Define estimate settings type
export interface EstimateSettings {
  taxRate: number;
  surchargeRate: number;
  markupRate: number;
  shippingRate: number;
  otherFee: number;
  roundUpGlassSize: boolean;
}

// Define customer information type
export interface CustomerInfo {
  companyName: string;
  companyAddress: string;
  companyCity: string;
  companyState: string;
  companyZip: string;
  contactName: string;
  contactPhone: string;
  projectName: string;
  projectAddress: string;
  projectCity: string;
  projectState: string;
  projectZip: string;
  useProjectAddress: boolean;
}

// Define context type
interface EstimateContextType {
  estimateItems: EstimateItem[];
  addEstimateItem: (item: Omit<EstimateItem, 'id'>) => void;
  updateEstimateItem: (id: string, item: Partial<EstimateItem>) => void;
  removeEstimateItem: (id: string) => void;
  clearEstimateItems: () => void;
  loadEstimate: (items: EstimateItem[], name?: string, id?: string, customerInfo?: CustomerInfo, images?: EstimateImage[]) => void;
  settings: EstimateSettings;
  updateSettings: (newSettings: Partial<EstimateSettings>) => void;
  taxRate: number; // Kept for backward compatibility
  setTaxRate: (rate: number) => void; // Kept for backward compatibility
  currentEstimateName: string;
  setCurrentEstimateName: (name: string) => void;
  currentEstimateId: string | null;
  setCurrentEstimateId: (id: string | null) => void;
  customerInfo: CustomerInfo;
  updateCustomerInfo: (info: Partial<CustomerInfo>) => void;
  isEditing: boolean;
  // Image management
  estimateImages: EstimateImage[];
  addEstimateImages: (images: EstimateImage[]) => Promise<void>;
  removeEstimateImage: (imageId: string) => Promise<void>;
  updateEstimateImageDescription: (imageId: string, description: string) => Promise<void>;
}

// Create context
const EstimateContext = createContext<EstimateContextType | undefined>(undefined);

// Create provider component
export const EstimateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State for estimate items
  const [estimateItems, setEstimateItems] = useState<EstimateItem[]>([]);

  // State for estimate settings
  const [settings, setSettings] = useState<EstimateSettings>({
    taxRate: 8.25, // Default tax rate
    surchargeRate: 0, // Default surcharge rate
    markupRate: 0, // Default markup rate
    shippingRate: 15, // Default shipping rate (15%)
    otherFee: 0, // Default other fee
    roundUpGlassSize: false, // Default round up glass size setting
  });

  // For backward compatibility
  const [taxRate, setTaxRate] = useState<number>(settings.taxRate);

  // State for current estimate name and ID
  const [currentEstimateName, setCurrentEstimateName] = useState<string>('');
  const [currentEstimateId, setCurrentEstimateId] = useState<string | null>(null);

  // State for customer information
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    companyName: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    contactName: '',
    contactPhone: '',
    projectName: '',
    projectAddress: '',
    projectCity: '',
    projectState: '',
    projectZip: '',
    useProjectAddress: false
  });

  // State for estimate images
  const [estimateImages, setEstimateImages] = useState<EstimateImage[]>([]);

  // Load estimate items and settings from localStorage on mount
  useEffect(() => {
    const savedItems = localStorage.getItem('estimateItems');
    const savedSettings = localStorage.getItem('estimateSettings');
    const savedTaxRate = localStorage.getItem('taxRate'); // For backward compatibility

    if (savedItems) {
      try {
        setEstimateItems(JSON.parse(savedItems));
      } catch (error) {
        console.error('Error loading estimate items from localStorage:', error);
      }
    }

    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(parsedSettings);
        setTaxRate(parsedSettings.taxRate); // Sync with taxRate state
      } catch (error) {
        console.error('Error loading estimate settings from localStorage:', error);
      }
    } else if (savedTaxRate) {
      // Backward compatibility
      try {
        const parsedTaxRate = parseFloat(savedTaxRate);
        setTaxRate(parsedTaxRate);
        setSettings(prev => ({ ...prev, taxRate: parsedTaxRate }));
      } catch (error) {
        console.error('Error loading tax rate from localStorage:', error);
      }
    }
  }, []);

  // Save estimate items to localStorage when they change
  useEffect(() => {
    localStorage.setItem('estimateItems', JSON.stringify(estimateItems));
  }, [estimateItems]);

  // Save tax rate to localStorage when it changes (for backward compatibility)
  useEffect(() => {
    localStorage.setItem('taxRate', taxRate.toString());
    // Also update the settings object
    setSettings(prev => ({ ...prev, taxRate }));
  }, [taxRate]);

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('estimateSettings', JSON.stringify(settings));
  }, [settings]);

  // Add a new estimate item
  const addEstimateItem = (item: Omit<EstimateItem, 'id'>) => {
    const newItem: EstimateItem = {
      ...item,
      id: generateUUID(),
    };

    // Calculate adjusted dimensions if measurement type is DLO
    if (item.measurementType === 'DLO') {
      // Get the glass thickness (as a number or string)
      const thickness = item.glassType.thickness;
      const thicknessStr = item.glassType.thicknessStr;

      // Check if it's 1" glass
      const isOneInchGlass =
        thickness === 1 ||
        thickness === '1' ||
        thicknessStr === '1' ||
        thicknessStr === '1"';

      // Check if it's 1/4" glass
      const isQuarterInchGlass =
        thickness === 0.25 ||
        thickness === '1/4' ||
        thicknessStr === '1/4' ||
        thicknessStr === '1/4"';

      if (item.frameType === 'Storefront') {
        if (isOneInchGlass) {
          // For 1" glass with Storefront: Add 3/4" to width and height
          newItem.adjustedWidth = item.width + 0.75;
          newItem.adjustedHeight = item.height + 0.75;
        } else if (isQuarterInchGlass && item.storefrontDLOAddition) {
          // For 1/4" glass with Storefront: Use the selected DLO addition
          const additionValue = item.storefrontDLOAddition === '1/2' ? 0.5 : 0.625;
          newItem.adjustedWidth = item.width + additionValue;
          newItem.adjustedHeight = item.height + additionValue;
        } else {
          // For other glass with Storefront: Add 1/2" to width and height (default)
          newItem.adjustedWidth = item.width + 0.5;
          newItem.adjustedHeight = item.height + 0.5;
        }
      } else if (item.frameType === 'Curtain Wall') {
        if (item.curtainWallType === 'Regular') {
          if (isOneInchGlass) {
            // For 1" glass with Curtain Wall Regular: Add 1" to width and height
            newItem.adjustedWidth = item.width + 1;
            newItem.adjustedHeight = item.height + 1;
          } else if (isQuarterInchGlass) {
            // For 1/4" glass with Curtain Wall Regular: Add 3/4" to width and height
            newItem.adjustedWidth = item.width + 0.75;
            newItem.adjustedHeight = item.height + 0.75;
          } else {
            // Default for other glass types
            newItem.adjustedWidth = item.width + 1;
            newItem.adjustedHeight = item.height + 1;
          }
        } else if (item.curtainWallType === 'SSG') {
          // For Curtain Wall SSG: Add 1-1/2" to width and 1-1/4" to height
          newItem.adjustedWidth = item.width + 1.5;
          newItem.adjustedHeight = item.height + 1.25;
        }
      }
    } else {
      // For Glass Size: Use exact measurements
      newItem.adjustedWidth = item.width;
      newItem.adjustedHeight = item.height;
    }

    setEstimateItems(prevItems => [...prevItems, newItem]);
  };

  // Update an existing estimate item
  const updateEstimateItem = (id: string, updatedFields: Partial<EstimateItem>) => {
    setEstimateItems(prevItems =>
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, ...updatedFields };

          // Recalculate adjusted dimensions if necessary
          if (
            updatedFields.width !== undefined ||
            updatedFields.height !== undefined ||
            updatedFields.measurementType !== undefined ||
            updatedFields.frameType !== undefined ||
            updatedFields.curtainWallType !== undefined
          ) {
            const width = updatedFields.width !== undefined ? updatedFields.width : item.width;
            const height = updatedFields.height !== undefined ? updatedFields.height : item.height;
            const measurementType = updatedFields.measurementType !== undefined ? updatedFields.measurementType : item.measurementType;
            const frameType = updatedFields.frameType !== undefined ? updatedFields.frameType : item.frameType;
            const curtainWallType = updatedFields.curtainWallType !== undefined ? updatedFields.curtainWallType : item.curtainWallType;

            if (measurementType === 'DLO') {
              // Get the glass thickness (as a number or string)
              const thickness = updatedItem.glassType.thickness;
              const thicknessStr = updatedItem.glassType.thicknessStr;

              // Check if it's 1" glass
              const isOneInchGlass =
                thickness === 1 ||
                thickness === '1' ||
                thicknessStr === '1' ||
                thicknessStr === '1"';

              // Check if it's 1/4" glass
              const isQuarterInchGlass =
                thickness === 0.25 ||
                thickness === '1/4' ||
                thicknessStr === '1/4' ||
                thicknessStr === '1/4"';

              if (frameType === 'Storefront') {
                if (isOneInchGlass) {
                  // For 1" glass with Storefront: Add 3/4" to width and height
                  updatedItem.adjustedWidth = width + 0.75;
                  updatedItem.adjustedHeight = height + 0.75;
                } else if (isQuarterInchGlass && updatedItem.storefrontDLOAddition) {
                  // For 1/4" glass with Storefront: Use the selected DLO addition
                  const additionValue = updatedItem.storefrontDLOAddition === '1/2' ? 0.5 : 0.625;
                  updatedItem.adjustedWidth = width + additionValue;
                  updatedItem.adjustedHeight = height + additionValue;
                } else {
                  // For other glass with Storefront: Add 1/2" to width and height (default)
                  updatedItem.adjustedWidth = width + 0.5;
                  updatedItem.adjustedHeight = height + 0.5;
                }
              } else if (frameType === 'Curtain Wall') {
                if (curtainWallType === 'Regular') {
                  if (isOneInchGlass) {
                    // For 1" glass with Curtain Wall Regular: Add 1" to width and height
                    updatedItem.adjustedWidth = width + 1;
                    updatedItem.adjustedHeight = height + 1;
                  } else if (isQuarterInchGlass) {
                    // For 1/4" glass with Curtain Wall Regular: Add 3/4" to width and height
                    updatedItem.adjustedWidth = width + 0.75;
                    updatedItem.adjustedHeight = height + 0.75;
                  } else {
                    // Default for other glass types
                    updatedItem.adjustedWidth = width + 1;
                    updatedItem.adjustedHeight = height + 1;
                  }
                } else if (curtainWallType === 'SSG') {
                  // For Curtain Wall SSG: Add 1-1/2" to width and 1-1/4" to height
                  updatedItem.adjustedWidth = width + 1.5;
                  updatedItem.adjustedHeight = height + 1.25;
                }
              }
            } else {
              // For Glass Size: Use exact measurements
              updatedItem.adjustedWidth = width;
              updatedItem.adjustedHeight = height;
            }
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  // Remove an estimate item
  const removeEstimateItem = (id: string) => {
    setEstimateItems(prevItems => prevItems.filter(item => item.id !== id));
  };

  // Clear all estimate items
  const clearEstimateItems = () => {
    setEstimateItems([]);
    setCurrentEstimateName('');
    setCurrentEstimateId(null);
  };

  // Load an estimate for editing
  const loadEstimate = async (items: EstimateItem[], name: string = '', id: string | null = null, customerData?: CustomerInfo, images?: EstimateImage[]) => {
    setEstimateItems(items);
    setCurrentEstimateName(name);
    setCurrentEstimateId(id);
    if (customerData) {
      setCustomerInfo(customerData);
    }

    // Load images if provided, otherwise try to load from IndexedDB
    if (images) {
      setEstimateImages(images);
    } else if (id) {
      try {
        const loadedImages = await getImagesForEstimate(id);
        setEstimateImages(loadedImages);
      } catch (error) {
        console.error('Error loading images for estimate:', error);
        setEstimateImages([]);
      }
    } else {
      setEstimateImages([]);
    }
  };

  // Update customer information
  const updateCustomerInfo = (info: Partial<CustomerInfo>) => {
    setCustomerInfo(prev => ({ ...prev, ...info }));
  };

  // Update estimate settings
  const updateSettings = (newSettings: Partial<EstimateSettings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      // Keep taxRate in sync for backward compatibility
      if (newSettings.taxRate !== undefined) {
        setTaxRate(newSettings.taxRate);
      }
      return updated;
    });
  };

  // Add images to the current estimate
  const addEstimateImages = async (images: EstimateImage[]) => {
    if (!currentEstimateId) {
      // If no estimate ID yet, just add to state (will be saved with estimate later)
      setEstimateImages(prev => [...prev, ...images]);
      return;
    }

    try {
      // Save each image to IndexedDB
      const savedImages = [];
      for (const image of images) {
        const { id, timestamp, ...imageData } = image;
        const savedImage = await saveImage(currentEstimateId, imageData);
        savedImages.push(savedImage);
      }

      // Update state with saved images
      setEstimateImages(prev => [...prev, ...savedImages]);
    } catch (error) {
      console.error('Error saving images:', error);
      throw new Error('Failed to save images');
    }
  };

  // Remove an image from the current estimate
  const removeEstimateImage = async (imageId: string) => {
    try {
      // Remove from IndexedDB if we have an estimate ID
      if (currentEstimateId) {
        await deleteImage(imageId);
      }

      // Remove from state
      setEstimateImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Error removing image:', error);
      throw new Error('Failed to remove image');
    }
  };

  // Update an image description
  const updateEstimateImageDescription = async (imageId: string, description: string) => {
    try {
      // Update in IndexedDB if we have an estimate ID
      if (currentEstimateId) {
        await updateImageDescription(imageId, description);
      }

      // Update in state
      setEstimateImages(prev =>
        prev.map(img =>
          img.id === imageId ? { ...img, description } : img
        )
      );
    } catch (error) {
      console.error('Error updating image description:', error);
      throw new Error('Failed to update image description');
    }
  };

  // Context value
  const value = {
    estimateItems,
    addEstimateItem,
    updateEstimateItem,
    removeEstimateItem,
    clearEstimateItems,
    loadEstimate,
    settings,
    updateSettings,
    taxRate,
    setTaxRate,
    currentEstimateName,
    setCurrentEstimateName,
    currentEstimateId,
    setCurrentEstimateId,
    customerInfo,
    updateCustomerInfo,
    isEditing: currentEstimateId !== null,
    // Image management
    estimateImages,
    addEstimateImages,
    removeEstimateImage,
    updateEstimateImageDescription,
  };

  return (
    <EstimateContext.Provider value={value}>
      {children}
    </EstimateContext.Provider>
  );
};

// Custom hook for using the estimate context
export const useEstimate = () => {
  const context = useContext(EstimateContext);
  if (context === undefined) {
    throw new Error('useEstimate must be used within an EstimateProvider');
  }
  return context;
};
